/** Client-side real-time statutory calculators (mirrors backend). */

/*
 * Fallbacks only — the screen passes the ceilings returned by GET /statutory/module (epf_rules),
 * which come from backend/services/epf_statutory_rules.py. EPF ceiling ₹25,000 from 17 Sep 2026
 * (S.O. 5109(E)); EPS / EDLI ceilings remain ₹15,000.
 */
export const DEFAULT_PF_WAGE_CEILING = 25000;
export const DEFAULT_EPS_WAGE_CEILING = 15000;
export const DEFAULT_EDLI_WAGE_CEILING = 15000;

const round2 = (v) => Math.round(v * 100) / 100;

export function calcPfLocal({
  basicPay = 0,
  da = 0,
  isCapped = true,
  wageCap = DEFAULT_PF_WAGE_CEILING,
  epsCap = DEFAULT_EPS_WAGE_CEILING,
  edliCap = DEFAULT_EDLI_WAGE_CEILING,
}) {
  const basic = Number(basicPay) || 0;
  const daVal = Number(da) || 0;
  const cap = Number(wageCap) || DEFAULT_PF_WAGE_CEILING;
  const gross = basic + daVal;
  const pfWage = isCapped ? Math.min(gross, cap) : gross;
  const r = (pct, base = pfWage) => round2((base * pct) / 100);
  const employeePf = r(12);
  const employerPf = r(12);
  // EPS is limited to the EPS pensionable ceiling; the EPF share is the rest of employer PF.
  const eps = Math.min(r(8.33, Math.min(pfWage, Number(epsCap) || DEFAULT_EPS_WAGE_CEILING)), employerPf);
  const epfShare = round2(employerPf - eps);
  const edli = r(0.5, Math.min(pfWage, Number(edliCap) || DEFAULT_EDLI_WAGE_CEILING));
  const admin = r(0.5);
  const inspection = r(0.01);
  const warnings = [];
  if (pfWage < cap) {
    warnings.push(`Employees with PF wages below ₹${cap.toLocaleString("en-IN")} are statutorily eligible.`);
  }
  return {
    pf_wage: pfWage,
    employee_pf: employeePf,
    employer_pf: employerPf,
    eps,
    epf_share: epfShare,
    edli,
    admin_charges: admin,
    inspection_charges: inspection,
    total_employer_cost: Math.round((employerPf + edli + admin + inspection) * 100) / 100,
    warnings,
  };
}

export function calcEsiLocal(grossSalary) {
  const gross = Number(grossSalary) || 0;
  if (gross > 21000) {
    return {
      eligible: false,
      gross_pay: gross,
      employee_contribution: 0,
      employer_contribution: 0,
      total_cost: 0,
      message: "Employee Not Eligible For ESI",
    };
  }
  const emp = Math.round(gross * 0.0075 * 100) / 100;
  const er = Math.round(gross * 0.0325 * 100) / 100;
  return {
    eligible: true,
    gross_pay: gross,
    employee_contribution: emp,
    employer_contribution: er,
    total_cost: Math.round((emp + er) * 100) / 100,
    message: null,
  };
}

// PT is calculated by the backend statutory rule master (POST /statutory/calculate/pt).

export function calcBonusLocal({ basicDa = 0, bonusPercent = 8.33, workedDays = 30 }) {
  const base = Number(basicDa) || 0;
  const eligible = base <= 21000 && workedDays >= 30;
  const eligibleSalary = Math.min(base, 21000);
  const monthly = eligible ? Math.round((eligibleSalary * bonusPercent) / 100 * 100) / 100 : 0;
  return {
    eligible,
    eligible_salary: eligibleSalary,
    bonus_percent: bonusPercent,
    monthly_bonus: monthly,
    annual_bonus: monthly * 12,
  };
}

export function fmtINR(n) {
  const v = Number(n);
  if (Number.isNaN(v)) return "₹0";
  return `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

export function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
